import React from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../App';
import { FileText, Mail, Phone } from 'lucide-react';

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/30 rounded-full mb-6">
            <FileText className="w-4 h-4 text-primary" />
            <span className="text-primary text-sm font-bold uppercase tracking-wider">Terms of Service</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-4">
            Terms of Service
          </h1>
          <p className="text-muted-foreground">
            Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>

        <div className="prose prose-invert max-w-none space-y-8">
          <section className="bg-card border border-border rounded-xl p-8">
            <h2 className="text-2xl font-heading font-bold mb-4">Agreement to Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              By accessing or using the services provided by Luck or Skills ("we", "our", or "us"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
            </p>
          </section>

          <section className="bg-card border border-border rounded-xl p-8">
            <h2 className="text-2xl font-heading font-bold mb-4">Description of Services</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Luck or Skills provides an educational poker learning community, including:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li>• Live poker strategy sessions and discussions</li>
              <li>• Poker lessons and educational content</li>
              <li>• Community forum for poker discussion</li>
              <li>• Hand review services</li>
              <li>• Access to archived livestreams</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              <strong className="text-foreground">Important:</strong> Luck or Skills is an educational platform. We do not provide gambling services, facilitate real-money poker games, or endorse gambling activities.
            </p>
          </section>

          <section className="bg-card border border-border rounded-xl p-8">
            <h2 className="text-2xl font-heading font-bold mb-4">Membership and Fees</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Access to certain features requires a paid membership. By subscribing, you agree to:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li>• Pay the applicable membership fees</li>
              <li>• Automatic renewal unless cancelled</li>
              <li>• Our cancellation and refund policy</li>
              <li>• Price changes with advance notice</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              You may cancel your membership at any time. Cancellations take effect at the end of the current billing period.
            </p>
          </section>

          <section className="bg-card border border-border rounded-xl p-8">
            <h2 className="text-2xl font-heading font-bold mb-4">User Conduct</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              You agree not to:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li>• Violate any laws or regulations</li>
              <li>• Post inappropriate, offensive, or harmful content</li>
              <li>• Harass or harm other users</li>
              <li>• Impersonate others</li>
              <li>• Attempt to gain unauthorized access to our systems</li>
              <li>• Use our services for commercial purposes without permission</li>
              <li>• Share your account credentials</li>
            </ul>
          </section>

          <section className="bg-card border border-border rounded-xl p-8">
            <h2 className="text-2xl font-heading font-bold mb-4">Intellectual Property</h2>
            <p className="text-muted-foreground leading-relaxed">
              All content on Luck or Skills, including text, graphics, logos, videos, and software, is the property of Luck or Skills or its content suppliers and is protected by intellectual property laws. You may not reproduce, distribute, modify, or create derivative works without our written permission.
            </p>
          </section>

          <section className="bg-card border border-border rounded-xl p-8">
            <h2 className="text-2xl font-heading font-bold mb-4">SMS Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              By providing your phone number and opting in to SMS communications, you agree to receive text messages from Luck or Skills regarding appointment confirmations, reminders, and service updates. SMS consent is optional and not required to use our services. Message and data rates may apply. You may opt out at any time by replying STOP. For more information, see our Privacy Policy.
            </p>
          </section>

          <section className="bg-card border border-border rounded-xl p-8">
            <h2 className="text-2xl font-heading font-bold mb-4">Disclaimers</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Our services are provided "as is" without warranties of any kind. We do not guarantee:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li>• Uninterrupted or error-free service</li>
              <li>• Specific results from using our educational content</li>
              <li>• Success in poker games or tournaments</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              <strong className="text-foreground">Gambling Disclaimer:</strong> Luck or Skills provides poker education only. We do not guarantee winnings or success in any poker game. Gambling may be illegal in your jurisdiction. It is your responsibility to know and follow your local laws.
            </p>
          </section>

          <section className="bg-card border border-border rounded-xl p-8">
            <h2 className="text-2xl font-heading font-bold mb-4">Limitation of Liability</h2>
            <p className="text-muted-foreground leading-relaxed">
              To the maximum extent permitted by law, Luck or Skills shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from your use of our services.
            </p>
          </section>

          <section className="bg-card border border-border rounded-xl p-8">
            <h2 className="text-2xl font-heading font-bold mb-4">Termination</h2>
            <p className="text-muted-foreground leading-relaxed">
              We reserve the right to suspend or terminate your access to our services at any time, with or without notice, for any reason, including violation of these Terms of Service.
            </p>
          </section>

          <section className="bg-card border border-border rounded-xl p-8">
            <h2 className="text-2xl font-heading font-bold mb-4">Changes to Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may modify these Terms of Service at any time. We will notify you of any changes by posting the new Terms of Service on this page and updating the "Last Updated" date. Your continued use of our services after such changes constitutes acceptance of the new terms.
            </p>
          </section>

          <section className="bg-card border border-border rounded-xl p-8">
            <h2 className="text-2xl font-heading font-bold mb-4">Governing Law</h2>
            <p className="text-muted-foreground leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of the State of Texas, without regard to its conflict of law provisions.
            </p>
          </section>

          <section className="bg-card border border-border rounded-xl p-8">
            <h2 className="text-2xl font-heading font-bold mb-4">Contact Information</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              For questions about these Terms of Service, please contact:
            </p>
            <div className="space-y-2 text-muted-foreground">
              <p><strong className="text-foreground">Luck or Skills</strong></p>
              <p>New Braunfels, TX 78130</p>
              <p>
                <Mail className="inline w-4 h-4 mr-2" />
                <a href="mailto:info@luckorskills.com" className="text-primary hover:text-primary/80">info@luckorskills.com</a>
              </p>
              <p>
                <Phone className="inline w-4 h-4 mr-2" />
                <a href="tel:+18302372821" className="text-primary hover:text-primary/80">(830) 237-2821</a>
              </p>
            </div>
          </section>
        </div>

        <div className="mt-12 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-semibold"
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
